import { Metric } from './Metric/Metric';
import { useOrganizerMetrics } from './useOrganizerMetrics';
import { useIntl } from 'react-intl';
import styles from './Metrics.module.scss';
import { useFollowContext } from '../../_shared/useFollowContext';

const MIN_FOLLOWERS_TO_DISPLAY = 10;
const MAX_DISPLAYED_EVENTS = 3_000;

interface Props {
  organizerId: string;
  hideAttendeeCount?: boolean;
  hideTotalEvents?: boolean;
}

export function Metrics({
  organizerId,
  hideAttendeeCount = false,
  hideTotalEvents = false,
}: Props) {
  const intl = useIntl();
  const { data: qs } = useOrganizerMetrics(organizerId);
  const { numFollowers } = useFollowContext();

  const showFollowers =
    typeof numFollowers === 'number' && numFollowers >= MIN_FOLLOWERS_TO_DISPLAY;
  const showHostingYears = qs?.hostingYears != null;
  const totalEvents = qs?.totalEvents;
  const showTotalEvents = totalEvents != null && !hideTotalEvents;
  const showAttendees = qs?.attendeesHosted != null && !hideAttendeeCount;

  if (!showFollowers && !showHostingYears && !showTotalEvents && !showAttendees) {
    return null;
  }

  return (
    <div className={styles.metrics} data-testid="organizer-metrics">
      {showFollowers && (
        <div className={styles.metricItem}>
          <Metric
            value={formatCount(numFollowers)}
            label={intl.formatMessage({ id: 'followers', defaultMessage: 'followers' })}
          />
        </div>
      )}
      {showHostingYears && (
        <div className={styles.metricItem}>
          <Metric
            value={qs.hostingYears}
            label={intl.formatMessage({
              id: 'hosting on Eventbrite',
              defaultMessage: 'hosting on Eventbrite',
            })}
          />
        </div>
      )}
      {showTotalEvents && (
        <div className={styles.metricItem}>
          <Metric
            value={formatCount(totalEvents, MAX_DISPLAYED_EVENTS)}
            label={intl.formatMessage({ id: 'total events', defaultMessage: 'total events' })}
          />
        </div>
      )}
      {showAttendees && (
        <div className={styles.metricItem}>
          <Metric
            value={qs.attendeesHosted}
            label={intl.formatMessage({ id: 'total attendees', defaultMessage: 'total attendees' })}
          />
        </div>
      )}
    </div>
  );
}

export function formatCount(n: number, max?: number): string {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (max != null && n > max) return `${formatCount(max)}+`;
  if (n >= 10_000_000) {
    const plus = n % 1_000_000 !== 0 ? '+' : '';
    return `${Math.floor(n / 1_000_000)}m${plus}`;
  }
  if (n >= 1_000_000) {
    const plus = n % 100_000 !== 0 ? '+' : '';
    return `${Math.floor(n / 100_000) / 10}m${plus}`;
  }
  if (n >= 10_000) {
    const plus = n % 1_000 !== 0 ? '+' : '';
    return `${Math.floor(n / 1_000)}k${plus}`;
  }
  if (n >= 1_000) {
    const plus = n % 100 !== 0 ? '+' : '';
    return `${Math.floor(n / 100) / 10}k${plus}`;
  }
  return String(Math.round(n));
}
